/**
 * CacheKeyGenerator - Utility for generating consistent cache keys
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides consistent cache key generation for page caching,
 * ensuring all cache operations use the same key format throughout the codebase.
 */

/**
 * Generate a cache key for a page
 * 
 * Format: `${docHash}-${pageIndex}`
 * 
 * @param docHash - Document hash (computed from text and font settings)
 * @param pageIndex - Zero-based page index
 * @returns Cache key string
 */
export function generatePageCacheKey(docHash: string, pageIndex: number): string {
  return `${docHash}-${pageIndex}`;
}

/**
 * Generate a cache key for a page range (for pagination requests)
 * 
 * Format: `${docHash}:${startPage}-${endPage}:${fontUid}:${fontSizePx}`
 * 
 * @param docHash - Document hash
 * @param startPage - Starting page index (inclusive)
 * @param endPage - Ending page index (inclusive)
 * @param fontUid - Font UID
 * @param fontSizePx - Font size in pixels
 * @returns Cache key string for pagination range
 */
export function generatePaginationRangeKey(
  docHash: string,
  startPage: number,
  endPage: number,
  fontUid: string,
  fontSizePx: number
): string {
  return `${docHash}:${startPage}-${endPage}:${fontUid}:${fontSizePx}`;
}

/**
 * Parse a page cache key to extract components
 * 
 * @param cacheKey - Cache key in format `${docHash}-${pageIndex}`
 * @returns Object with docHash and pageIndex, or null if invalid format
 */
export function parsePageCacheKey(cacheKey: string): { docHash: string; pageIndex: number } | null {
  const parts = cacheKey.split('-');
  if (parts.length < 2) {
    return null;
  }
  
  // Handle docHash that might contain hyphens (unlikely but possible)
  // Last part is always pageIndex
  const pageIndex = parseInt(parts[parts.length - 1], 10);
  if (isNaN(pageIndex)) {
    return null;
  }
  
  // Everything before the last part is docHash
  const docHash = parts.slice(0, -1).join('-');
  
  return { docHash, pageIndex };
}

/**
 * Extract page index from a cache key
 * 
 * @param cacheKey - Cache key in format `${docHash}-${pageIndex}`
 * @returns Page index, or null if invalid format
 */
export function extractPageIndexFromKey(cacheKey: string): number | null {
  const parsed = parsePageCacheKey(cacheKey);
  return parsed ? parsed.pageIndex : null;
}

